import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useSelector } from "react-redux"
import Api from "../../api/api"
import { meSelector } from "../../redux/globalSlice"
import { Organization, Sku, Product, UserRole } from "../../api/models"
import Spinner from "../widgets/Spinner"
import OrgChip, { orgLogoIcon } from "../widgets/OrgChip"
import CardImage from "../widgets/CardImage"
import ProductName from "../widgets/ProductName"
import ChipSelect from "../widgets/ChipSelect"
import { buildImageSrcSet } from "../widgets/imageSrcSet"
import CopyableId from "../widgets/CopyableId"
import { alIcon, creditsIcon, trialIcon } from "../widgets/pillIcons"
import AddProductDialog from "../AddProductDialog"

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 2

// A dollar amount without trailing .00 noise ($99, $79.50)
function formatValue(valueCents: number): string {
  return "$" + (valueCents % 100 === 0 ? String(valueCents / 100) : (valueCents / 100).toFixed(2))
}

// The SkuRow design: image column, name (the logo when it has one) with a
// muted value affix, one pill per capability (AI assistant, pay-as-you-go,
// trial), org chip at the right edge; the skus the product belongs to as
// clickable thumbnail chips — a product in no sku is called out (it's
// invisible on the store).
function ProductRow({ product, skus }: { product: Product, skus: Sku[] }) {
  const navigate = useNavigate()

  return (
    <div
      className="license-group license-group-with-img"
      style={{ cursor: "pointer" }}
      onClick={() => navigate(`/products/${product.id}`)}
    >
      <div className="license-group-img-container">
        <CardImage imageUrl={product.imageUrl} availableWidths={product.availableWidths} alt={product.name} />
      </div>
      {/* pills flow left right after the name (the class's space-between is
          overridden); the org chip's own margin-left:auto sends it right */}
      <div className="product-group-header" style={{ justifyContent: "flex-start", flexWrap: "wrap", rowGap: 4 }}>
        {product.imageUrl && (
          <img
            src={product.imageUrl}
            srcSet={buildImageSrcSet(product.imageUrl, product.availableWidths)}
            sizes="44px"
            alt=""
            className="product-header-icon"
          />
        )}
        <h2 className="section-title product-row-title">
          <ProductName product={product} variant="section" />
          {product.valueCents > 0 && (
            <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt", fontWeight: "normal", marginLeft: 8 }}>
              {formatValue(product.valueCents)}
            </span>
          )}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {product.al != null && (
            <span className="badge badge-logo" title="Supports the A.L. assistant">{alIcon}</span>
          )}
          {product.needsCredits && (
            <span className="badge">{creditsIcon}Pay-as-you-go</span>
          )}
          {product.trialDays !== undefined && product.trialDays > 0 && (
            <span className="badge">{trialIcon}{product.trialDays}-day trial</span>
          )}
        </div>
        <OrgChip orgId={product.ownerId} />
        {/* the id breaks onto its own full-width line under the name row */}
        <div style={{ flexBasis: "100%" }}>
          <CopyableId id={product.id} />
        </div>
      </div>
      <div className="product-skus">
        {skus.length > 0 ? (
          <>
            <h3 className="product-skus-heading">Included in SKUs:</h3>
            <div className="product-sku-chips">
              {skus.map((it) => (
                <span
                  key={it.id}
                  className={"product-sku-chip" + (it.imageUrl ? "" : " product-sku-chip-noimg")}
                  onClick={(e) => { e.stopPropagation(); navigate(`/skus/${it.id}`) }}
                >
                  {it.imageUrl && (
                    <img
                      src={it.imageUrl}
                      srcSet={buildImageSrcSet(it.imageUrl, it.availableWidths)}
                      sizes="22px"
                      alt=""
                      className="product-sku-chip-img"
                    />
                  )}
                  {it.name}
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="product-skus-heading" style={{ margin: 0 }}>
            Not included in any released SKU — a released SKU version's claims pull it in.
          </p>
        )}
      </div>
    </div>
  )
}

// Server-side name search (GET /admin/products?search=), ≥2 chars, debounced.
// Local state like Users/SkuEntitlements — see the note there about the
// singleton redux pagination helpers.
export default function Products() {
  const session = useSession()
  const accessToken = session.accessToken ?? ""

  // the ownerId filter is admin-only on the backend (a Developer's listing is
  // already scoped to their own products)
  const isAdmin = useSelector(meSelector)?.data?.role === UserRole.Admin

  const navigate = useNavigate()

  const [searchText, setSearchText] = useState("")
  const [ownerFilter, setOwnerFilter] = useState("")
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [addProductOpen, setAddProductOpen] = useState(false)
  const [products, setProducts] = useState<Product[] | undefined>(undefined)
  const [skusById, setSkusById] = useState<Map<string, Sku>>(() => new Map())
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // stale responses (criteria changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  const search = searchText.trim().length >= SEARCH_MIN_CHARS ? searchText.trim() : undefined
  const ownerId = ownerFilter || undefined

  useEffect(() => {
    if (!isAdmin || !accessToken) return
    let stale = false
    Api.listOrganizations({ accessToken })
      .then(({ organizations }) => { if (!stale) setOrganizations(organizations) })
      .catch(() => { })
    return () => { stale = true }
  }, [isAdmin, accessToken])

  // cursor === null loads the first page (replacing state), a cursor appends
  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.getProducts({ accessToken, search, ownerId, link: cursor ?? undefined, count: PAGE_SIZE })
      if (seq !== requestSeq.current) return
      setProducts((prev) => cursor === null ? page.products : [...(prev ?? []), ...page.products])
      setSkusById((prev) => {
        const next = cursor === null ? new Map<string, Sku>() : new Map(prev)
        page.skus.forEach((it) => next.set(it.id, it))
        return next
      })
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [accessToken, search, ownerId])

  useEffect(() => {
    requestSeq.current++    // discard any in-flight page of the old criteria
    setProducts(undefined)
    setLink(null)
    // debounce keystrokes only; the unfiltered listing loads immediately
    const timer = setTimeout(() => loadPage(null), search !== undefined ? SEARCH_DEBOUNCE_MS : 0)
    return () => clearTimeout(timer)
  }, [loadPage, search])

  const isLoading = products === undefined && !error

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Products</h1>
        <button
          className="card-link"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          onClick={() => setAddProductOpen(true)}
        >
          Add product
        </button>
      </div>

      <div className="filter-bar">
        <input
          className="filter-input"
          type="text"
          placeholder="product name…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {isAdmin && (
          <ChipSelect
            value={ownerFilter}
            onChange={setOwnerFilter}
            options={organizations.map((org) => org.id)}
            labels={Object.fromEntries(organizations.map((org) => [org.id, org.name]))}
            icons={Object.fromEntries(organizations.map((org) => [org.id, orgLogoIcon(org)]))}
            placeholder="All organizations"
          />
        )}
      </div>

      {error && products === undefined && (
        <p className="empty-state">Error loading products</p>
      )}

      {isLoading && (
        <p className="empty-state"><Spinner size={16} /> Loading products…</p>
      )}

      {!isLoading && !error && products!.length === 0 && (
        search !== undefined || ownerFilter !== "" ? (
          <p className="empty-state">No matching products</p>
        ) : (
          <div className="empty-state-block">
            <span>No products yet</span>
            <span className="empty-state-hint">Use the "Add product" button to create your first product</span>
          </div>
        )
      )}

      {(products ?? []).map((p) => (
        <ProductRow
          key={p.id}
          product={p}
          skus={p.skuIds.map((id) => skusById.get(id)).filter((it): it is Sku => it != null)}
        />
      ))}

      {link !== null && products !== undefined && (
        loading ? <Spinner size={16} /> : (
          <button className="card-link" onClick={() => loadPage(link)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            Load more
          </button>
        )
      )}
      {error && products !== undefined && (
        <p className="empty-state">Error loading more products</p>
      )}

      {addProductOpen && (
        <AddProductDialog
          accessToken={accessToken}
          isAdmin={isAdmin}
          onClose={() => setAddProductOpen(false)}
          onCreated={(product) => navigate(`/products/${product.id}`)}
        />
      )}
    </div>
  )
}
